import React from 'react';
import { Typography, Box, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import madrasatiLightLogo from '../../assets/madrasati-light.svg';
import madrasatiDarkLogo from '../../assets/madrasati-dark.svg';

const PrivacyPolicy = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        backgroundColor: theme.palette.secondary.main,
        overflow: 'hidden',
      }}
    >
      {/* Decorative circles */}
      <Box
        sx={{
          position: 'absolute',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, rgba(52, 152, 219, 0.09) 0%, rgba(41, 128, 185, 0.15) 100%)`,
          top: '-100px',
          right: '-100px',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, rgba(41, 128, 185, 0.12) 0%, rgba(52, 152, 219, 0.18) 100%)`,
          bottom: '50px',
          left: '50px',
        }}
      />

      {/* Content Container */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '800px',
          margin: '0 auto',
          padding: { xs: 3, md: 5 },
          zIndex: 1,
        }}
      >
        {/* Logo at top */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 4,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              borderRadius: '24px',
              border: '2px solid transparent',
              background: 'transparent',
              transition: 'all 0.3s ease-in-out',
              padding: { xs: 1.5, md: 2 },
              display: 'inline-block',
              '&:hover': {
                border: '2px solid rgba(255, 255, 255, 0.3)',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
              },
            }}
          >
            <Box
              component="img"
              src={theme.palette.mode === 'dark' ? madrasatiDarkLogo : madrasatiLightLogo}
              alt="Madrasati Logo"
              onClick={() => navigate('/')}
              sx={{
                width: '100%',
                maxWidth: '200px',
                height: 'auto',
                objectFit: 'contain',
                cursor: 'pointer',
                transition: 'opacity 0.2s ease-in-out, transform 0.2s ease-in-out',
                display: 'block',
                '&:hover': {
                  opacity: 0.8,
                  transform: 'scale(1.05)',
                },
              }}
            />
          </Box>
        </Box>

        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: theme.typography.fontWeightBold,
            color: theme.palette.secondary.contrastText,
            mb: 2,
            textAlign: 'center',
          }}
        >
          Privacy Policy
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.secondary.contrastText,
            opacity: 0.8,
            mb: 4,
            textAlign: 'center',
          }}
        >
          Last Updated: {new Date().toLocaleDateString()}
        </Typography>

        {/* Centered content sections */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: '600px',
            margin: '0 auto',
          }}
        >
          <Box sx={{ mb: 4, width: '100%' }}>
            <Typography
              variant="body1"
              paragraph
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              Madrasati ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services.
            </Typography>
          </Box>

          <Box sx={{ mb: 4, width: '100%' }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              1. Information We Collect
            </Typography>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ color: theme.palette.secondary.contrastText, mt: 2 }}
            >
              1.1 Personal Information
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              We collect information that you provide directly to us, including:
            </Typography>
            <Box component="ul" sx={{ pl: 3, color: theme.palette.secondary.contrastText }}>
              <li>Name and contact information (email address, phone number)</li>
              <li>Account credentials</li>
              <li>Student information (for educational purposes)</li>
              <li>Payment information (processed securely through our payment providers)</li>
              <li>Communications with us</li>
            </Box>

            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ mt: 2, color: theme.palette.secondary.contrastText }}
            >
              1.2 Automatically Collected Information
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              When you use our services, we automatically collect:
            </Typography>
            <Box component="ul" sx={{ pl: 3, color: theme.palette.secondary.contrastText }}>
              <li>Device information (type, operating system, browser)</li>
              <li>Usage data (interactions with our services)</li>
              <li>IP address and location data</li>
              <li>Cookies and similar tracking technologies</li>
            </Box>
          </Box>

          <Box sx={{ mb: 4, width: '100%' }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              2. How We Use Your Information
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              We use the collected information for:
            </Typography>
            <Box component="ul" sx={{ pl: 3, color: theme.palette.secondary.contrastText }}>
              <li>Providing and maintaining our services</li>
              <li>Processing payments and sending transaction notifications</li>
              <li>Sending administrative messages and updates</li>
              <li>Responding to your comments and questions</li>
              <li>Analyzing usage patterns to improve our services</li>
              <li>Protecting against fraudulent or illegal activity</li>
            </Box>
          </Box>

          <Box sx={{ mb: 4, width: '100%' }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              3. Information Sharing and Disclosure
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              We do not sell your personal information. We may share your information with:
            </Typography>
            <Box component="ul" sx={{ pl: 3, color: theme.palette.secondary.contrastText }}>
              <li>Service providers who assist in operating our services</li>
              <li>Law enforcement when required by law</li>
              <li>Other parties with your explicit consent</li>
            </Box>
          </Box>

          <Box sx={{ mb: 4, width: '100%' }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              4. Data Security
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              We implement appropriate technical and organizational measures to protect your personal information, including:
            </Typography>
            <Box component="ul" sx={{ pl: 3, color: theme.palette.secondary.contrastText }}>
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments</li>
              <li>Access controls and authentication requirements</li>
              <li>Regular backup procedures</li>
            </Box>
          </Box>

          <Box sx={{ mb: 4, width: '100%' }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              5. Your Rights and Choices
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              You have the right to:
            </Typography>
            <Box component="ul" sx={{ pl: 3, color: theme.palette.secondary.contrastText }}>
              <li>Access your personal information</li>
              <li>Correct inaccurate information</li>
              <li>Request deletion of your information</li>
              <li>Opt-out of marketing communications</li>
              <li>Update your communication preferences</li>
            </Box>
          </Box>

          <Box sx={{ mb: 4, width: '100%' }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              6. Children's Privacy
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              We comply with the Children's Online Privacy Protection Act (COPPA). We do not knowingly collect personal information from children under 18 without parental consent. If you believe we have inadvertently collected such information, please contact us to request deletion.
            </Typography>
          </Box>

          <Box sx={{ mb: 4, width: '100%' }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              7. Changes to This Policy
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </Typography>
          </Box>

          <Box sx={{ mb: 4, width: '100%' }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              8. Contact Us
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              If you have questions about this Privacy Policy, please contact us at:
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: theme.palette.secondary.contrastText }}
            >
              Email: techtalentedllc@gmail.com
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PrivacyPolicy; 